import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CONTACT_EMAIL = "charyl@glowgrow.app";
const APP_NAME = "Glow & Grow";
const APP_URL = "glowgrow.app";

/* ============================================================
   Shared shell used by all legal / contact static pages.
   ============================================================ */
function LegalShell({ title, kicker, children }) {
  return (
    <div className="min-h-screen bg-cream">
      <div className="max-w-3xl mx-auto px-6 pt-6 pb-24">
        <Link
          to="/"
          data-testid="legal-back-home"
          className="inline-flex items-center gap-2 text-sm text-cocoa/60 hover:text-cocoa"
        >
          <ArrowLeft size={16} /> Back to Glow &amp; Grow
        </Link>
        <p className="text-[11px] uppercase tracking-[0.25em] text-rose font-medium mt-8 mb-1">
          {kicker}
        </p>
        <h1 className="font-serif text-4xl md:text-5xl text-cocoa mb-8 leading-tight">
          {title}
        </h1>
        <div className="prose prose-sm max-w-none text-cocoa/80 font-light leading-relaxed space-y-5">
          {children}
        </div>
        <div className="mt-16 pt-8 border-t border-cocoa/10">
          <p className="text-xs text-cocoa/50 font-light">
            Questions? Reach us at{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-rose font-medium"
              data-testid="legal-contact-email"
            >
              {CONTACT_EMAIL}
            </a>
            .
          </p>
          <p className="text-xs text-cocoa/40 font-light mt-2">
            © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}

function H2({ children }) {
  return (
    <h2 className="font-serif text-2xl text-cocoa mt-8 mb-2">{children}</h2>
  );
}

/* ============================================================
   Terms of Service
   ============================================================ */
export function Terms() {
  return (
    <LegalShell kicker="Legal" title="Terms of Service">
      <p className="text-cocoa/55 text-xs uppercase tracking-wider">
        Last updated: February 2026
      </p>

      <p>
        Welcome to {APP_NAME} ({APP_URL}). These Terms of Service (&quot;Terms&quot;)
        govern your access to and use of our website, mobile applications, and
        related services (the &quot;Service&quot;). By creating an account, subscribing,
        or otherwise using the Service, you agree to be bound by these Terms.
      </p>

      <H2>1. Who we are</H2>
      <p>
        {APP_NAME} is a personalized beauty subscription platform that generates
        skincare and haircare routines, DIY beauty recipes, product dupe
        matches, and daily rituals adapted to each user&apos;s skin and hair
        profile. Our editorial and product content is for informational and
        wellness purposes only; it is not medical advice.
      </p>

      <H2>2. Eligibility</H2>
      <p>
        You must be at least 16 years old (or the age of digital consent in your
        country, whichever is higher) to use the Service. By using the Service
        you represent that all information you provide is accurate and that you
        have the legal capacity to enter into these Terms.
      </p>

      <H2>3. Accounts</H2>
      <p>
        You are responsible for keeping your account credentials confidential and
        for all activity that occurs under your account. Notify us immediately
        at <a href={`mailto:${CONTACT_EMAIL}`} className="text-rose">{CONTACT_EMAIL}</a>{" "}
        if you suspect unauthorized access.
      </p>

      <H2>4. Subscriptions &amp; billing</H2>
      <p>
        Premium features are available via a subscription in the following
        plans, priced in <strong>US dollars (USD)</strong>:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Monthly:</strong> $12/month</li>
        <li><strong>Yearly:</strong> $89/year (≈ $7.42/month, save 38%)</li>
      </ul>
      <p>
        Subscriptions renew automatically at the end of each billing period at
        the then-current price unless cancelled. You can cancel at any time from
        the Premium page; access continues until the end of the paid period. See
        our <Link to="/refund" className="text-rose font-medium">Refund Policy</Link>{" "}
        for details.
      </p>

      <H2>5. Free trial &amp; promotions</H2>
      <p>
        We may occasionally offer trials or promotional pricing. Trials
        automatically convert to a paid subscription unless cancelled before
        the trial ends.
      </p>

      <H2>6. Acceptable use</H2>
      <p>
        You agree not to: (a) reverse-engineer, scrape, or misuse the Service or
        our AI models; (b) upload content that is illegal, defamatory,
        infringing, or hateful; (c) impersonate another person; (d) attempt to
        circumvent premium gating, rate limits, or security controls; or (e)
        use the Service to build a competing product.
      </p>

      <H2>7. Content &amp; intellectual property</H2>
      <p>
        All content generated by {APP_NAME}, including editorial articles,
        product data, routines, and DIY recipes, is owned by us or our licensors
        and is protected by copyright and other intellectual-property laws. You
        receive a limited, personal, non-transferable license to use the Service
        for your own beauty journey.
      </p>

      <H2>8. User content</H2>
      <p>
        You retain ownership of content you upload (e.g., progress photos, notes,
        contact-form messages). By uploading, you grant us a non-exclusive,
        worldwide license to store and process that content solely to operate
        and improve the Service.
      </p>

      <H2>9. AI-assisted recommendations disclaimer</H2>
      <p>
        Routines, product dupes, and Glow Reports are generated by algorithms
        and large language models. They are personalized suggestions, not
        medical advice. Always patch-test new products and consult a licensed
        dermatologist or trichologist for medical concerns, pregnancy, or
        serious reactions.
      </p>

      <H2>10. Third-party services</H2>
      <p>
        The Service integrates with third-party providers (payments, hosting,
        email, analytics). Their use of your data is governed by their own
        policies.
      </p>

      <H2>11. Termination</H2>
      <p>
        We may suspend or terminate your account if you breach these Terms. You
        may delete your account at any time from the Profile page.
      </p>

      <H2>12. Warranty disclaimer</H2>
      <p>
        The Service is provided &quot;as is&quot; and &quot;as available&quot; without warranties
        of any kind. To the fullest extent permitted by law, we disclaim implied
        warranties of merchantability, fitness for a particular purpose, and
        non-infringement.
      </p>

      <H2>13. Limitation of liability</H2>
      <p>
        To the extent permitted by law, {APP_NAME} is not liable for indirect,
        incidental, special, consequential, or exemplary damages, or for lost
        profits or data. Our total liability in any 12-month period will not
        exceed the amounts you paid to us during that period.
      </p>

      <H2>14. Changes to these Terms</H2>
      <p>
        We may update these Terms from time to time. Material changes will be
        notified by email or in-app banner. Continued use after the effective
        date constitutes acceptance.
      </p>

      <H2>15. Governing law &amp; disputes</H2>
      <p>
        These Terms are governed by the laws of the jurisdiction in which
        {" "}{APP_NAME} is registered, without regard to conflict-of-laws principles.
        Disputes will first be attempted to be resolved via good-faith email
        exchange with our team at{" "}
        <a href={`mailto:${CONTACT_EMAIL}`} className="text-rose">{CONTACT_EMAIL}</a>.
      </p>

      <H2>16. Contact</H2>
      <p>
        Questions about these Terms:{" "}
        <a href={`mailto:${CONTACT_EMAIL}`} className="text-rose">{CONTACT_EMAIL}</a>.
      </p>
    </LegalShell>
  );
}

/* ============================================================
   Privacy Policy — GDPR + POPIA style
   ============================================================ */
export function Privacy() {
  return (
    <LegalShell kicker="Legal" title="Privacy Policy">
      <p className="text-cocoa/55 text-xs uppercase tracking-wider">
        Last updated: February 2026
      </p>

      <p>
        This Privacy Policy explains how {APP_NAME} (&quot;we&quot;, &quot;us&quot;) collects,
        uses, and protects your personal information when you use{" "}
        {APP_URL} or our mobile app. We take your privacy seriously and this
        policy is written to comply with the EU General Data Protection
        Regulation (GDPR), the South African Protection of Personal Information
        Act (POPIA), and equivalent global consumer-privacy laws.
      </p>

      <H2>1. Data controller</H2>
      <p>
        {APP_NAME} is the &quot;data controller&quot; / &quot;responsible party&quot; for the
        personal information described here. You can reach us at{" "}
        <a href={`mailto:${CONTACT_EMAIL}`} className="text-rose">{CONTACT_EMAIL}</a>.
      </p>

      <H2>2. What we collect</H2>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Account data:</strong> name, email address, hashed password.</li>
        <li>
          <strong>Beauty profile:</strong> skin type, skin tone, hair type,
          hair texture, beauty concerns, goals, allergies — you choose what to
          share during onboarding.
        </li>
        <li>
          <strong>Usage data:</strong> routines completed, challenges joined,
          articles read, saved recipes, dupe searches, streaks — used to
          personalize your experience.
        </li>
        <li>
          <strong>Progress content:</strong> optional photos and notes you
          upload to your Glow journal. Stored privately in an access-controlled
          bucket.
        </li>
        <li>
          <strong>Device / technical data:</strong> browser type, IP address
          (short-term), device model, crash logs.
        </li>
        <li>
          <strong>Payment data:</strong> processed by our PCI-compliant
          payment provider — we never see or store your full card number.
        </li>
      </ul>

      <H2>3. Legal basis (GDPR Art. 6)</H2>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Consent</strong> — for optional profile fields and marketing emails (you can withdraw anytime).</li>
        <li><strong>Contract</strong> — to provide the Service you subscribed to.</li>
        <li><strong>Legitimate interest</strong> — to prevent abuse, secure the platform, and improve features.</li>
        <li><strong>Legal obligation</strong> — for tax, billing records, and lawful requests.</li>
      </ul>

      <H2>4. How we use your data</H2>
      <ul className="list-disc pl-5 space-y-1">
        <li>Generate personalized skincare &amp; haircare routines and DIY recipes.</li>
        <li>Match beauty products and dupes to your unique profile.</li>
        <li>Send transactional emails (receipts, password resets) and, with consent, ritual reminders.</li>
        <li>Improve accuracy of our recommendations (aggregated, non-identifying).</li>
        <li>Detect abuse, enforce rate limits, and secure the platform.</li>
      </ul>

      <H2>5. AI processing</H2>
      <p>
        Some features (Dupe Finder, Glow Report, Adaptive Routines) send
        de-identified prompts to third-party language models. We do not send
        your name, email, photos, or payment data to these models. You can
        opt out of AI features by not subscribing to Premium.
      </p>

      <H2>6. Who we share data with</H2>
      <p>
        We share only what is strictly necessary with vetted processors under
        data-processing agreements:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Hosting &amp; database:</strong> secure cloud infrastructure providers.</li>
        <li><strong>AI &amp; language processing:</strong> language-model providers to power personalized recommendations.</li>
        <li><strong>Payment processing:</strong> PCI-compliant payment providers for subscription billing.</li>
        <li><strong>Email delivery:</strong> transactional email providers.</li>
        <li><strong>Push notifications:</strong> mobile push-notification providers.</li>
      </ul>
      <p>
        We never sell your personal data. We do not share it with advertisers.
      </p>

      <H2>7. International transfers</H2>
      <p>
        Your data may be processed in the United States, the European Union, or
        South Africa. Transfers are protected by Standard Contractual Clauses
        or equivalent safeguards.
      </p>

      <H2>8. Retention</H2>
      <p>
        We retain account data as long as your account is active. On deletion,
        your beauty profile and progress content are removed within 30 days,
        except for records we must keep for legal or tax purposes.
      </p>

      <H2>9. Your rights</H2>
      <p>Under GDPR, POPIA, and similar laws, you have the right to:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Access the personal information we hold about you.</li>
        <li>Correct inaccurate data.</li>
        <li>Delete (&quot;right to be forgotten&quot;) your account and data.</li>
        <li>Restrict or object to processing.</li>
        <li>Portability — receive your data in a common format.</li>
        <li>Withdraw consent for marketing at any time.</li>
        <li>Complain to your local data-protection authority.</li>
      </ul>
      <p>
        Exercise any of these rights by writing to{" "}
        <a href={`mailto:${CONTACT_EMAIL}`} className="text-rose">{CONTACT_EMAIL}</a>.
        We respond within 30 days.
      </p>

      <H2>10. Children</H2>
      <p>
        The Service is not directed at children under 16. We do not knowingly
        collect data from anyone under that age. If we learn we have, we will
        delete it.
      </p>

      <H2>11. Security</H2>
      <p>
        We use encryption in transit (HTTPS), encrypted-at-rest storage,
        row-level security on our database, strict access controls, private
        storage buckets with signed URLs, and Content-Security-Policy headers
        to protect your data.
      </p>

      <H2>12. Cookies</H2>
      <p>
        We use only essential cookies required to keep you logged in and
        remember your consent choices. We do not run behavioural-advertising
        cookies.
      </p>

      <H2>13. Changes to this policy</H2>
      <p>
        We may update this policy. Material changes will be notified in-app or
        via email at least 14 days before they take effect.
      </p>

      <H2>14. Contact</H2>
      <p>
        Privacy questions or requests:{" "}
        <a href={`mailto:${CONTACT_EMAIL}`} className="text-rose">{CONTACT_EMAIL}</a>.
      </p>
    </LegalShell>
  );
}

/* ============================================================
   Refund Policy
   ============================================================ */
export function Refund() {
  return (
    <LegalShell kicker="Legal" title="Refund Policy">
      <p className="text-cocoa/55 text-xs uppercase tracking-wider">
        Last updated: February 2026
      </p>

      <p>
        We want your Glow &amp; Grow experience to feel seamless — including how
        billing works. This Refund Policy explains when refunds are and are not
        available.
      </p>

      <H2>1. Cancel anytime</H2>
      <p>
        You can cancel your subscription at any moment from the Premium page in
        the app. Cancellation is immediate — your subscription will not renew
        after cancellation.
      </p>

      <H2>2. Access continues until the end of your paid period</H2>
      <p>
        When you cancel, you keep full access to premium features until the end
        of your current billing period (monthly or yearly, whichever plan you
        are on). After that date, your account reverts to the free tier and no
        further charges are made.
      </p>

      <H2>3. Refunds are generally not provided</H2>
      <p>
        Because you retain access for the full period you have paid for, we do
        not issue refunds for cancellations, unused time, or partial months on
        our monthly or yearly plans. Please review the plan details before
        subscribing.
      </p>

      <H2>4. When we <em>do</em> issue refunds</H2>
      <p>Refunds are provided in the following limited cases:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>An accidental duplicate charge on your card.</li>
        <li>Erroneous or fraudulent billing not authorized by you.</li>
        <li>Where a mandatory local consumer-protection law requires a refund.</li>
      </ul>

      <H2>5. How to request a review</H2>
      <p>
        For billing disputes, please email{" "}
        <a href={`mailto:${CONTACT_EMAIL}`} className="text-rose">{CONTACT_EMAIL}</a>{" "}
        from the email address associated with your account within 30 days of
        the charge. Please include:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>The date and amount of the charge.</li>
        <li>The last four digits of the payment card used.</li>
        <li>A brief description of the issue.</li>
      </ul>
      <p>
        We review every request personally and reply within 5 business days.
        Approved refunds are returned to the original payment method within 5
        to 10 business days.
      </p>

      <H2>6. Chargebacks</H2>
      <p>
        If you believe a charge is incorrect, please contact us first — we can
        usually resolve it faster than a bank dispute. Chargebacks may result in
        immediate suspension of your account until the matter is resolved.
      </p>

      <H2>7. Contact</H2>
      <p>
        Billing disputes:{" "}
        <a href={`mailto:${CONTACT_EMAIL}`} className="text-rose">{CONTACT_EMAIL}</a>.
      </p>
    </LegalShell>
  );
}

/* ============================================================
   Contact page — functional mailto: form
   ============================================================ */
export function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  // The form opens the user's mail client with the message pre-composed. This
  // is the most reliable "no-server-required" delivery — no lost messages,
  // and the user's own email address is used for direct reply.
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setBusy(true);
    const mailtoSubject = encodeURIComponent(subject || `Message from ${name}`);
    const mailtoBody = encodeURIComponent(
      `${message}\n\n— ${name}\nReply-to: ${email}`,
    );
    try {
      window.location.href = `mailto:${CONTACT_EMAIL}?subject=${mailtoSubject}&body=${mailtoBody}`;
      setTimeout(() => {
        setSent(true);
        setBusy(false);
      }, 500);
    } catch {
      // Fallback: copy the email + message to clipboard.
      try {
        await navigator.clipboard.writeText(
          `To: ${CONTACT_EMAIL}\nSubject: ${subject}\n\n${message}`,
        );
        toast.success("We couldn't open your mail app — the message is copied to your clipboard.");
      } catch {
        toast("Please email us at " + CONTACT_EMAIL);
      }
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <LegalShell kicker="Say hello" title="Message on its way">
        <div className="rounded-3xl bg-white p-8 text-center border border-[#f0e8e4]">
          <CheckCircle2 className="mx-auto text-rose mb-3" size={40} />
          <p className="font-serif text-2xl text-cocoa mb-2">Thank you!</p>
          <p className="text-sm text-cocoa/60 font-light">
            Your email app should have opened with your message ready to send.
            If it didn&apos;t, you can email us directly at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-rose font-medium">
              {CONTACT_EMAIL}
            </a>
            . We reply within 2 business days.
          </p>
          <button
            onClick={() => setSent(false)}
            data-testid="contact-send-another"
            className="mt-6 rounded-full bg-rose text-white px-6 py-2.5 text-sm font-medium"
          >
            Send another
          </button>
        </div>
      </LegalShell>
    );
  }

  return (
    <LegalShell kicker="Say hello" title="Contact us">
      <p>
        We&apos;d love to hear from you — feedback, feature requests, billing
        questions, or partnership ideas.
      </p>

      <div className="rounded-3xl bg-white p-6 border border-[#f0e8e4] my-6" data-testid="contact-email-card">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-rose/10 flex items-center justify-center">
            <Mail size={20} className="text-rose" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-cocoa/50 font-medium">
              Email us directly
            </p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="font-serif text-xl text-cocoa"
              data-testid="contact-direct-email"
            >
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
        <input
          type="text"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="contact-name"
          required
          className="w-full rounded-full bg-white border border-cocoa/10 px-5 py-3.5 text-sm placeholder:text-cocoa/40 focus:outline-none focus:ring-2 focus:ring-rose/40 text-cocoa"
        />
        <input
          type="email"
          placeholder="Your email (so we can reply)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="contact-email-input"
          required
          className="w-full rounded-full bg-white border border-cocoa/10 px-5 py-3.5 text-sm placeholder:text-cocoa/40 focus:outline-none focus:ring-2 focus:ring-rose/40 text-cocoa"
        />
        <input
          type="text"
          placeholder="Subject (optional)"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          data-testid="contact-subject"
          className="w-full rounded-full bg-white border border-cocoa/10 px-5 py-3.5 text-sm placeholder:text-cocoa/40 focus:outline-none focus:ring-2 focus:ring-rose/40 text-cocoa"
        />
        <textarea
          placeholder="Your message"
          rows={6}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          data-testid="contact-message"
          required
          className="w-full rounded-3xl bg-white border border-cocoa/10 px-5 py-3.5 text-sm placeholder:text-cocoa/40 focus:outline-none focus:ring-2 focus:ring-rose/40 text-cocoa resize-none"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="contact-submit"
          className="w-full rounded-full bg-rose hover:bg-rose-dark text-white py-3.5 font-medium shadow-rose transition-all inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : "Send message"}
        </button>
        <p className="text-[11px] text-cocoa/45 text-center font-light">
          Sending opens your default email app with the message ready.
        </p>
      </form>
    </LegalShell>
  );
}
